'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient, clearStaleAuthTokens } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function isRefreshTokenError(error: any): boolean {
  return (
    error?.code === 'refresh_token_not_found' || error?.message?.includes('Refresh Token Not Found') ||
    error?.message?.includes('Invalid Refresh Token')
  );
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  // Prevent re-entrant token error handling
  const handlingTokenError = useRef(false);

  const handleTokenError = () => {
    if (handlingTokenError.current) return;
    handlingTokenError.current = true;
    clearStaleAuthTokens();
    setSession(null);
    setUser(null);
    setLoading(false);
    // Sign out silently without throwing — ignore errors
    supabase.auth.signOut().catch(() => {}).finally(() => {
      handlingTokenError.current = false;
    });
  };

  useEffect(() => {
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Get initial session — use getSession (no network call, reads from storage)
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error && isRefreshTokenError(error)) {
        handleTokenError();
        return;
      }
      if (error) {
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }
      // If session exists, verify the token is still valid
      if (session) {
        supabase.auth.getUser().then(({ error: userError }) => {
          if (userError && isRefreshTokenError(userError)) {
            handleTokenError();
          } else if (!userError) {
            setSession(session);
            setUser(session.user ?? null);
            setLoading(false);
          } else {
            setSession(null);
            setUser(null);
            setLoading(false);
          }
        }).catch((err) => {
          if (isRefreshTokenError(err)) {
            handleTokenError();
          } else {
            setSession(null);
            setUser(null);
            setLoading(false);
          }
        });
      } else {
        setSession(null);
        setUser(null);
        setLoading(false);
      }
    }).catch((err) => {
      if (isRefreshTokenError(err)) {
        handleTokenError();
      } else {
        setSession(null);
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    clearStaleAuthTokens();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
  };

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:165:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="165" data-component-end-line="165" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>;
};
